import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "./api"
import type {
  User,
  DocumentGroup,
  DocumentDetail,
  DocumentSummary,
  Admin,
  AdminOrganization,
  AdminUser,
  AdminSpeciaAdmin,
  AdminDocument,
  AdminSection,
  AdminVersion,
  AdminWorkflowActivity,
  DocumentAssignment,
  WorkflowAssignment,
  ApiApplication,
  ApiApplicationWithSecret,
  OrgUser,
  OrgSettings,
  SubOrganization,
  SubOrganizationsResponse,
  SubOrgAssignment,
  Quiz,
  QuizVersion,
  Question,
  QuestionType,
  QuizAvailabilityRow,
  QuizFeedbackRow,
  AssignablePerson,
  Group,
  AssignableQuiz,
  ManagerQuizSetting,
  ManagerAssignmentRow,
  CreatedAssignments,
  AssignRequest,
  AssignPreview,
  QuizRoundRow,
  QuizScheduleSummary,
  StaffAssignmentRow,
  StartedAttempt,
  AttemptReview,
  AttemptSummary,
  AssignmentDetail,
  SubmitResult,
  Certificate,
  DashboardSummary,
} from "./types"

// Auth queries
export function useCurrentUser() {
  return useQuery<User>({
    queryKey: ["currentUser"],
    queryFn: () => api.get<User>("/api/v1/auth/me"),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

// The signed-in person naming themselves. Invalidates the session query so
// every surface reading `full_name` — the assessment gate above all — sees
// the new value without a reload.
export function useUpdateProfileName() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (name: string) =>
      api.patch<{ id: number; full_name: string; name: string }>(
        "/api/v1/customer/profile",
        { name }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] })
    },
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (credentials: { login: string; password: string }) =>
      api.post<User>("/api/v1/auth/session", credentials),
    onSuccess: (user) => {
      queryClient.setQueryData(["currentUser"], user)
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete("/api/v1/auth/session"),
    onSuccess: () => {
      queryClient.setQueryData(["currentUser"], null)
      queryClient.clear()
    },
  })
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: () => api.post("/api/v1/customer/password_reset"),
  })
}

export function useResetTokenUser(token: string | undefined) {
  return useQuery({
    queryKey: ["resetTokenUser", token],
    queryFn: () =>
      api.get<{ username: string }>(
        `/api/v1/auth/password?reset_password_token=${token}`
      ),
    enabled: !!token,
    retry: false,
  })
}

export function useResetPassword(adminMode = false) {
  return useMutation({
    mutationFn: (data: {
      reset_password_token: string
      password: string
      password_confirmation: string
    }) =>
      api.put<{ message: string }>(
        adminMode ? "/api/v1/admin/auth/password" : "/api/v1/auth/password",
        data
      ),
  })
}

// Document queries
export function useDocuments() {
  return useQuery<DocumentGroup[]>({
    queryKey: ["documents"],
    queryFn: () => api.get<DocumentGroup[]>("/api/v1/customer/documents"),
  })
}

export function useDocument(slug: string, clinicalReviewSlug?: string) {
  const params = clinicalReviewSlug
    ? `?clinical_review=${encodeURIComponent(clinicalReviewSlug)}`
    : ""
  return useQuery<DocumentDetail>({
    queryKey: ["document", slug, clinicalReviewSlug],
    queryFn: () =>
      api.get<DocumentDetail>(`/api/v1/customer/documents/${slug}${params}`),
    enabled: !!slug,
  })
}

// Admin Preview Documents queries
export function useAdminPreviewDocuments(documentType: string) {
  return useQuery<DocumentSummary[]>({
    queryKey: ["adminPreviewDocuments", documentType],
    queryFn: () =>
      api.get<DocumentSummary[]>(
        `/api/v1/admin/preview_documents?document_type=${encodeURIComponent(documentType)}`
      ),
  })
}

export function useAdminPreviewDocument(
  slug: string,
  clinicalReviewSlug?: string
) {
  const params = clinicalReviewSlug
    ? `?clinical_review=${encodeURIComponent(clinicalReviewSlug)}`
    : ""
  return useQuery<DocumentDetail>({
    queryKey: ["adminPreviewDocument", slug, clinicalReviewSlug],
    queryFn: () =>
      api.get<DocumentDetail>(
        `/api/v1/admin/preview_documents/${slug}${params}`
      ),
    enabled: !!slug,
  })
}

// Admin Auth queries
export function useCurrentAdmin() {
  return useQuery<Admin>({
    queryKey: ["currentAdmin"],
    queryFn: () => api.get<Admin>("/api/v1/admin/auth/me"),
    retry: false,
    staleTime: 5 * 60 * 1000,
  })
}

export function useAdminLogin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (credentials: { email: string; password: string }) =>
      api.post<Admin>("/api/v1/admin/auth/session", credentials),
    onSuccess: (admin) => {
      queryClient.setQueryData(["currentAdmin"], admin)
    },
  })
}

export function useAdminLogout() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete("/api/v1/admin/auth/session"),
    onSuccess: () => {
      queryClient.setQueryData(["currentAdmin"], null)
      queryClient.clear()
    },
  })
}

// Admin Organizations queries
export function useAdminOrganizations() {
  return useQuery<AdminOrganization[]>({
    queryKey: ["adminOrganizations"],
    queryFn: () =>
      api.get<AdminOrganization[]>("/api/v1/admin/organizations"),
  })
}

export function useCreateOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      name: string
      parent_organization_id?: number
      sso_domain?: string
      entra_tenant_id?: string
    }) =>
      api.post<AdminOrganization>("/api/v1/admin/organizations", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminOrganizations"] })
    },
  })
}

export function useUpdateOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; name?: string; active?: boolean; quizzes_enabled?: boolean; preview_mode?: boolean; api_access_enabled?: boolean; max_sub_organizations?: number; sso_domain?: string | null; entra_tenant_id?: string | null }) =>
      api.put<AdminOrganization>(
        `/api/v1/admin/organizations/${data.id}`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminOrganizations"] })
    },
  })
}

// Admin Users queries
export function useAdminUsers(orgId: number) {
  return useQuery<AdminUser[]>({
    queryKey: ["adminUsers", orgId],
    queryFn: () =>
      api.get<AdminUser[]>(`/api/v1/admin/organizations/${orgId}/users`),
    enabled: !!orgId,
  })
}

export function useCreateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    // No `name`: the server ignores it, and the person supplies their own
    // before their first assessment.
    mutationFn: (data: { orgId: number; username: string; email: string }) =>
      api.post<AdminUser>(
        `/api/v1/admin/organizations/${data.orgId}/users`,
        data
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminUsers", variables.orgId],
      })
      queryClient.invalidateQueries({ queryKey: ["adminOrganizations"] })
    },
  })
}

export function useUpdateUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      id: number
      orgId: number
      email?: string
      active?: boolean
      role?: "staff" | "manager"
    }) => api.put<AdminUser>(`/api/v1/admin/users/${data.id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminUsers", variables.orgId],
      })
      queryClient.invalidateQueries({ queryKey: ["adminOrganizations"] })
    },
  })
}

export function useDeleteUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; orgId: number }) =>
      api.delete<{ message: string }>(`/api/v1/admin/users/${data.id}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminUsers", variables.orgId],
      })
      queryClient.invalidateQueries({ queryKey: ["adminOrganizations"] })
    },
  })
}

export function useSendPasswordReset() {
  return useMutation({
    mutationFn: (userId: number) =>
      api.post<{ message: string }>(
        `/api/v1/admin/users/${userId}/send_password_reset`
      ),
  })
}

export function useUnlockUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; orgId: number }) =>
      api.post<AdminUser>(`/api/v1/admin/users/${data.id}/unlock`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminUsers", variables.orgId],
      })
    },
  })
}

// Admin Specia Admins queries
export function useAdminSpeciaAdmins() {
  return useQuery<AdminSpeciaAdmin[]>({
    queryKey: ["adminSpeciaAdmins"],
    queryFn: () =>
      api.get<AdminSpeciaAdmin[]>("/api/v1/admin/specia_admins"),
  })
}

export function useCreateSpeciaAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { email: string; role?: string; permissions?: string[] }) =>
      api.post<AdminSpeciaAdmin>("/api/v1/admin/specia_admins", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSpeciaAdmins"] })
    },
  })
}

export function useUpdateSpeciaAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      id: number
      email?: string
      active?: boolean
      role?: string
      permissions?: string[]
    }) =>
      api.put<AdminSpeciaAdmin>(
        `/api/v1/admin/specia_admins/${data.id}`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSpeciaAdmins"] })
    },
  })
}

export function useDeleteSpeciaAdmin() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/api/v1/admin/specia_admins/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminSpeciaAdmins"] })
    },
  })
}

export function useSendSpeciaAdminPasswordReset() {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ message: string }>(
        `/api/v1/admin/specia_admins/${id}/send_password_reset`
      ),
  })
}

// Admin Documents queries
export function useAdminDocuments(documentType?: string, scope?: string, search?: string) {
  const searchParams = new URLSearchParams()
  if (documentType) searchParams.set("document_type", documentType)
  if (scope) searchParams.set("scope", scope)
  if (search) searchParams.set("search", search)
  const qs = searchParams.toString()
  return useQuery<AdminDocument[]>({
    queryKey: ["adminDocuments", documentType, scope, search],
    queryFn: () =>
      api.get<AdminDocument[]>(`/api/v1/admin/documents${qs ? `?${qs}` : ""}`),
  })
}

export function useCreateDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      title: string
      document_type: string
      from_template?: string
      duplicate_from?: number
    }) => api.post<AdminDocument>("/api/v1/admin/documents", data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["adminDocuments", variables.document_type],
      })
      queryClient.invalidateQueries({ queryKey: ["adminDocuments", undefined] })
    },
  })
}

export function useUpdateDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; title?: string; compliance_tags?: string[]; keywords?: string[]; show_in_footer?: boolean }) =>
      api.put<AdminDocument>(`/api/v1/admin/documents/${data.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useDeleteDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, force }: { id: number; force?: boolean }) =>
      api.delete<{ message: string }>(
        `/api/v1/admin/documents/${id}${force ? "?force=true" : ""}`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useRestoreDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<AdminDocument>(`/api/v1/admin/documents/${id}/restore`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

// Admin Versions queries
export function useCreateVersion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (docId: number) =>
      api.post<AdminVersion>(`/api/v1/admin/documents/${docId}/versions`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useUpdateVersion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; change_summary?: string; published_at?: string }) =>
      api.put<AdminVersion>(`/api/v1/admin/versions/${data.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useDeleteVersion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/api/v1/admin/versions/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function usePublishVersion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; change_summary?: string; published_at?: string; admin_only?: boolean }) =>
      api.post<AdminVersion>(
        `/api/v1/admin/versions/${data.id}/publish`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
      queryClient.invalidateQueries({ queryKey: ["documents"] })
    },
  })
}

// Admin Sections queries
export function useCreateSection() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      versionId: number
      heading: string
      body_html: string
      template_key?: string
    }) =>
      api.post<AdminSection>(
        `/api/v1/admin/versions/${data.versionId}/sections`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useUpdateSection() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      id: number
      heading?: string
      body_html?: string
      lock_version?: number
    }) => api.put<AdminSection>(`/api/v1/admin/sections/${data.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useDeleteSection() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/api/v1/admin/sections/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useReorderSections() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { versionId: number; section_ids: number[] }) =>
      api.post<AdminSection[]>(
        `/api/v1/admin/versions/${data.versionId}/sections/reorder`,
        { section_ids: data.section_ids }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

// Admin Assignments queries
export function useAssignments(orgId: number) {
  return useQuery<DocumentAssignment[]>({
    queryKey: ["assignments", orgId],
    queryFn: () =>
      api.get<DocumentAssignment[]>(
        `/api/v1/admin/organizations/${orgId}/assignments`
      ),
    enabled: !!orgId,
  })
}

export function useUpdateAssignments() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      orgId: number
      assignments: Array<{ document_id: number; active: boolean }>
    }) =>
      api.put<DocumentAssignment[]>(
        `/api/v1/admin/organizations/${data.orgId}/assignments`,
        { assignments: data.assignments }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["assignments", variables.orgId],
      })
    },
  })
}

// Admin Workflow Assignments queries
export function useWorkflowAssignments(orgId: number) {
  return useQuery<WorkflowAssignment[]>({
    queryKey: ["workflowAssignments", orgId],
    queryFn: () =>
      api.get<WorkflowAssignment[]>(
        `/api/v1/admin/organizations/${orgId}/workflow_assignments`
      ),
    enabled: !!orgId,
  })
}

export function useUpdateWorkflowAssignments() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      orgId: number
      workflow_assignments: Array<{
        workflow_document_id: number
        clinical_review_document_id: number
        active: boolean
      }>
    }) =>
      api.put<WorkflowAssignment[]>(
        `/api/v1/admin/organizations/${data.orgId}/workflow_assignments`,
        { workflow_assignments: data.workflow_assignments }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["workflowAssignments", variables.orgId],
      })
    },
  })
}

// Admin Workflow Activities queries
export function useCreateActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { versionId: number; name: string }) =>
      api.post<AdminWorkflowActivity>(
        `/api/v1/admin/versions/${data.versionId}/activities`,
        { name: data.name }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useUpdateActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; name: string }) =>
      api.put<AdminWorkflowActivity>(
        `/api/v1/admin/activities/${data.id}`,
        { name: data.name }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useDeleteActivity() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/api/v1/admin/activities/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useReorderActivities() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { versionId: number; activity_ids: number[] }) =>
      api.post<AdminWorkflowActivity[]>(
        `/api/v1/admin/versions/${data.versionId}/activities/reorder`,
        { activity_ids: data.activity_ids }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useCreateActivitySection() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      activityId: number
      heading: string
      body_html: string
      template_key?: string
    }) =>
      api.post<AdminSection>(
        `/api/v1/admin/activities/${data.activityId}/sections`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

export function useReorderActivitySections() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { activityId: number; section_ids: number[] }) =>
      api.post<AdminSection[]>(
        `/api/v1/admin/activities/${data.activityId}/sections/reorder`,
        { section_ids: data.section_ids }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminDocuments"] })
    },
  })
}

// Customer API Applications queries
export function useApiApplications() {
  return useQuery<ApiApplication[]>({
    queryKey: ["apiApplications"],
    queryFn: () => api.get<ApiApplication[]>("/api/v1/customer/api_applications"),
  })
}

export function useCreateApiApplication() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string }) =>
      api.post<ApiApplicationWithSecret>("/api/v1/customer/api_applications", data),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["apiApplications"] }),
  })
}

export function useRotateApiApplication() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ApiApplicationWithSecret>(`/api/v1/customer/api_applications/${id}/rotate`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["apiApplications"] }),
  })
}

export function useRevokeApiApplication() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/api/v1/customer/api_applications/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["apiApplications"] }),
  })
}

// Admin API Applications queries
export function useAdminApiApplications(orgId: number) {
  return useQuery<ApiApplication[]>({
    queryKey: ["adminApiApplications", orgId],
    queryFn: () =>
      api.get<ApiApplication[]>(
        `/api/v1/admin/organizations/${orgId}/api_applications`
      ),
    enabled: !!orgId,
  })
}

export function useCreateAdminApiApplication(orgId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string }) =>
      api.post<ApiApplicationWithSecret>(
        `/api/v1/admin/organizations/${orgId}/api_applications`,
        data
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["adminApiApplications", orgId] }),
  })
}

export function useRotateAdminApiApplication(orgId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ApiApplicationWithSecret>(
        `/api/v1/admin/organizations/${orgId}/api_applications/${id}/rotate`
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["adminApiApplications", orgId] }),
  })
}

export function useRevokeAdminApiApplication(orgId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(
        `/api/v1/admin/organizations/${orgId}/api_applications/${id}`
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["adminApiApplications", orgId] }),
  })
}

// CR Template Keys query
export function useCrTemplateKeys() {
  return useQuery<Array<{ template_key: string; heading: string }>>({
    queryKey: ["crTemplateKeys"],
    queryFn: () =>
      api.get<Array<{ template_key: string; heading: string }>>(
        "/api/v1/admin/cr_template_keys"
      ),
    staleTime: 60 * 1000,
  })
}

// Org admin (customer self-service) queries
const orgUsersPath = (subOrgId?: number) =>
  subOrgId
    ? `/api/v1/customer/sub_organizations/${subOrgId}/users`
    : "/api/v1/customer/users"

export function useOrgUsers(subOrgId?: number) {
  return useQuery<OrgUser[]>({
    queryKey: ["orgUsers", subOrgId ?? "self"],
    queryFn: () => api.get<OrgUser[]>(orgUsersPath(subOrgId)),
  })
}

export function useInviteOrgUser(subOrgId?: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { email: string; role: string }) =>
      api.post<OrgUser>(orgUsersPath(subOrgId), data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orgUsers", subOrgId ?? "self"] })
      if (subOrgId) {
        queryClient.invalidateQueries({ queryKey: ["subOrganizations"] })
      }
    },
  })
}

export function useUpdateOrgUser(subOrgId?: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; role?: string; active?: boolean }) =>
      api.put<OrgUser>(`${orgUsersPath(subOrgId)}/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orgUsers", subOrgId ?? "self"] })
    },
  })
}

export function useSendOrgUserReset(subOrgId?: number) {
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ message: string }>(`${orgUsersPath(subOrgId)}/${id}/send_password_reset`),
  })
}

export function useUnlockOrgUser(subOrgId?: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<OrgUser>(`${orgUsersPath(subOrgId)}/${id}/unlock`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orgUsers", subOrgId ?? "self"] })
    },
  })
}

export function useOrgSettings() {
  return useQuery<OrgSettings>({
    queryKey: ["orgSettings"],
    queryFn: () => api.get<OrgSettings>("/api/v1/customer/organization"),
  })
}

export function useUpdateOrgSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { sso_domain?: string | null; entra_tenant_id?: string | null }) =>
      api.put<OrgSettings>("/api/v1/customer/organization", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orgSettings"] })
    },
  })
}

export function useSubOrganizations() {
  return useQuery<SubOrganizationsResponse>({
    queryKey: ["subOrganizations"],
    queryFn: () => api.get<SubOrganizationsResponse>("/api/v1/customer/sub_organizations"),
  })
}

export function useCreateSubOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; sso_domain?: string; entra_tenant_id?: string }) =>
      api.post<SubOrganization>("/api/v1/customer/sub_organizations", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subOrganizations"] })
    },
  })
}

export function useUpdateSubOrganization() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: {
      id: number
      name?: string
      sso_domain?: string | null
      entra_tenant_id?: string | null
      active?: boolean
    }) => api.put<SubOrganization>(`/api/v1/customer/sub_organizations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subOrganizations"] })
    },
  })
}

export function useSubOrgAssignments(subOrgId: number) {
  return useQuery<SubOrgAssignment[]>({
    queryKey: ["subOrgAssignments", subOrgId],
    queryFn: () =>
      api.get<SubOrgAssignment[]>(`/api/v1/customer/sub_organizations/${subOrgId}/assignments`),
  })
}

export function useUpdateSubOrgAssignments(subOrgId: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (assignments: { document_id: number; active: boolean }[]) =>
      api.put<SubOrgAssignment[]>(
        `/api/v1/customer/sub_organizations/${subOrgId}/assignments`,
        { assignments }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subOrgAssignments", subOrgId] })
    },
  })
}

// Admin Quizzes queries
export function useQuizzes(scope?: string) {
  const qs = scope ? `?scope=${encodeURIComponent(scope)}` : ""
  return useQuery<Quiz[]>({
    queryKey: ["adminQuizzes", scope],
    queryFn: () => api.get<Quiz[]>(`/api/v1/admin/quizzes${qs}`),
  })
}

export function useCreateQuiz() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { title: string; clinical_review_document_id?: number }) =>
      api.post<Quiz>("/api/v1/admin/quizzes", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminQuizzes"] })
    },
  })
}

export function useUpdateQuiz() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; title?: string; clinical_review_document_id?: number | null }) =>
      api.put<Quiz>(`/api/v1/admin/quizzes/${data.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminQuizzes"] })
    },
  })
}

export function useDeleteQuiz() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, force }: { id: number; force?: boolean }) =>
      api.delete<{ message: string }>(
        `/api/v1/admin/quizzes/${id}${force ? "?force=true" : ""}`
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminQuizzes"] })
    },
  })
}

export function useRestoreQuiz() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<Quiz>(`/api/v1/admin/quizzes/${id}/restore`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adminQuizzes"] })
    },
  })
}

// Admin Quiz Versions queries
export function useQuizVersions(quizId: number | null) {
  return useQuery<QuizVersion[]>({
    queryKey: ["quizVersions", quizId],
    queryFn: () =>
      api.get<QuizVersion[]>(`/api/v1/admin/quizzes/${quizId}/quiz_versions`),
    enabled: !!quizId,
  })
}

export function useCreateQuizVersion(quizId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      api.post<QuizVersion>(`/api/v1/admin/quizzes/${quizId}/quiz_versions`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quizVersions", quizId] })
      queryClient.invalidateQueries({ queryKey: ["adminQuizzes"] })
    },
  })
}

export function useUpdateQuizVersion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; label?: string }) =>
      api.put<QuizVersion>(`/api/v1/admin/quiz_versions/${data.id}`, data),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["quizVersions", result.quiz_id] })
    },
  })
}

export function usePublishQuizVersion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<QuizVersion>(`/api/v1/admin/quiz_versions/${id}/publish`),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["quizVersions", result.quiz_id] })
      queryClient.invalidateQueries({ queryKey: ["adminQuizzes"] })
    },
  })
}

export function useDeleteQuizVersion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; quizId: number }) =>
      api.delete<{ message: string }>(`/api/v1/admin/quiz_versions/${data.id}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["quizVersions", variables.quizId] })
      queryClient.invalidateQueries({ queryKey: ["adminQuizzes"] })
    },
  })
}

export function useQuizFeedback(quizId: number | null) {
  return useQuery<QuizFeedbackRow[]>({
    queryKey: ["quizFeedback", quizId],
    queryFn: () =>
      api.get<QuizFeedbackRow[]>(
        `/api/v1/admin/quizzes/${quizId}/quiz_feedbacks`
      ),
    enabled: !!quizId,
  })
}

// Admin Questions queries
export function useQuestions(versionId: number | null) {
  return useQuery<Question[]>({
    queryKey: ["questions", versionId],
    queryFn: () =>
      api.get<Question[]>(`/api/v1/admin/quiz_versions/${versionId}/questions`),
    enabled: !!versionId,
  })
}

export function useCreateQuestion(versionId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      prompt: string
      position?: number
      question_type?: QuestionType
      answer_options?: Array<{ text: string; correct: boolean; position: number }>
      correct_text?: string
    }) =>
      api.post<Question>(
        `/api/v1/admin/quiz_versions/${versionId}/questions`,
        data
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questions", versionId] })
    },
  })
}

export function useUpdateQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      id: number
      versionId: number
      prompt?: string
      question_type?: QuestionType
      answer_options?: Array<{ id?: number; text: string; correct: boolean; position: number }>
      correct_text?: string
    }) =>
      api.put<Question>(`/api/v1/admin/questions/${data.id}`, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["questions", variables.versionId] })
    },
  })
}

export function useDeleteQuestion() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { id: number; versionId: number }) =>
      api.delete<{ message: string }>(`/api/v1/admin/questions/${data.id}`),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["questions", variables.versionId] })
    },
  })
}

export function useReorderQuestions(versionId: number | null) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (orderedIds: number[]) =>
      api.post<Question[]>(
        `/api/v1/admin/quiz_versions/${versionId}/questions/reorder`,
        { ordered_ids: orderedIds }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["questions", versionId] })
    },
  })
}

// Admin Quiz Availability queries
export function useQuizAvailability(orgId: number) {
  return useQuery<QuizAvailabilityRow[]>({
    queryKey: ["quizAvailability", orgId],
    queryFn: () =>
      api.get<QuizAvailabilityRow[]>(
        `/api/v1/admin/organizations/${orgId}/quiz_availability`
      ),
    enabled: !!orgId,
  })
}

export function useUpdateQuizAvailability() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: {
      orgId: number
      availabilities: { quiz_id: number; active: boolean }[]
    }) =>
      api.put<QuizAvailabilityRow[]>(
        `/api/v1/admin/organizations/${data.orgId}/quiz_availability`,
        { availabilities: data.availabilities }
      ),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["quizAvailability", variables.orgId],
      })
    },
  })
}

// Manager: Assignable Quizzes query
export function useAssignablePeople() {
  return useQuery<AssignablePerson[]>({
    queryKey: ["assignablePeople"],
    queryFn: () =>
      api.get<AssignablePerson[]>("/api/v1/manager/assignable_people"),
  })
}

export function useGroups() {
  return useQuery<Group[]>({
    queryKey: ["groups"],
    queryFn: () => api.get<Group[]>("/api/v1/manager/groups"),
  })
}

export function useCreateGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: { name: string; member_ids: number[] }) =>
      api.post<Group>("/api/v1/manager/groups", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] })
    },
  })
}

export function useUpdateGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...data }: { id: number; name?: string; member_ids?: number[] }) =>
      api.put<Group>(`/api/v1/manager/groups/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] })
    },
  })
}

export function useDeleteGroup() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/api/v1/manager/groups/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["groups"] })
    },
  })
}

export function useAssignableQuizzes() {
  return useQuery<AssignableQuiz[]>({
    queryKey: ["assignableQuizzes"],
    queryFn: () =>
      api.get<AssignableQuiz[]>("/api/v1/manager/assignable_quizzes"),
  })
}

export function useManagerQuizSettings() {
  return useQuery<ManagerQuizSetting[]>({
    queryKey: ["managerQuizSettings"],
    queryFn: () => api.get<ManagerQuizSetting[]>("/api/v1/manager/quiz_settings"),
  })
}

export function useUpdateManagerQuizSettings() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (settings: { quiz_id: number; pass_count: number | null; max_attempts: number | null; open_book: boolean }[]) =>
      api.put<ManagerQuizSetting[]>("/api/v1/manager/quiz_settings", { settings }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["managerQuizSettings"] })
    },
  })
}

// Manager: Quiz Assignments queries
export function useManagerAssignments() {
  return useQuery<ManagerAssignmentRow[]>({
    queryKey: ["managerAssignments"],
    queryFn: () =>
      api.get<ManagerAssignmentRow[]>("/api/v1/manager/quiz_assignments"),
  })
}

export function useManagerRounds() {
  return useQuery<QuizRoundRow[]>({
    queryKey: ["managerRounds"],
    queryFn: () => api.get<QuizRoundRow[]>("/api/v1/manager/quiz_rounds"),
  })
}

// Dry run of useCreateAssignment: writes nothing, so nothing to invalidate.
export function useAssignPreview() {
  return useMutation({
    mutationFn: (data: AssignRequest) =>
      api.post<AssignPreview>("/api/v1/manager/quiz_assignments/preview", data),
  })
}

function invalidateRounds(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: ["managerAssignments"] })
  queryClient.invalidateQueries({ queryKey: ["managerRounds"] })
  queryClient.invalidateQueries({ queryKey: ["managerDashboard"] })
}

export function useCreateAssignment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: AssignRequest) =>
      api.post<CreatedAssignments>(
        "/api/v1/manager/quiz_assignments",
        data
      ),
    onSuccess: () => {
      invalidateRounds(queryClient)
      queryClient.invalidateQueries({ queryKey: ["assignableQuizzes"] })
      queryClient.invalidateQueries({ queryKey: ["groups"] })
    },
  })
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: number
      interval_months?: 6 | 12
      due_in_weeks?: number | null
      active?: boolean
    }) =>
      api.patch<QuizScheduleSummary>(
        `/api/v1/manager/quiz_schedules/${id}`,
        data
      ),
    onSuccess: () => {
      invalidateRounds(queryClient)
      queryClient.invalidateQueries({ queryKey: ["groups"] })
    },
  })
}

export function useDeleteAssignment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<{ message: string }>(`/api/v1/manager/quiz_assignments/${id}`),
    onSuccess: () => invalidateRounds(queryClient),
  })
}

export function useManagerAssignmentAttempts(id: number | undefined) {
  return useQuery<AttemptSummary[]>({
    queryKey: ["managerAssignmentAttempts", id],
    queryFn: () =>
      api.get<AttemptSummary[]>(
        `/api/v1/manager/quiz_assignments/${id}/attempts`
      ),
    enabled: !!id,
  })
}

export function useManagerDashboard() {
  return useQuery<DashboardSummary>({
    queryKey: ["managerDashboard"],
    queryFn: () => api.get<DashboardSummary>("/api/v1/manager/dashboard"),
  })
}

export function useReviewAssignment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ManagerAssignmentRow>(
        `/api/v1/manager/quiz_assignments/${id}/review`
      ),
    // A review can close a one-time round, so rounds refresh too.
    onSuccess: () => invalidateRounds(queryClient),
  })
}

export function useResetAttempts() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ManagerAssignmentRow>(`/api/v1/manager/quiz_assignments/${id}/reset_attempts`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["managerAssignments"] })
      queryClient.invalidateQueries({ queryKey: ["managerDashboard"] })
    },
  })
}

export function useManagerCertificate(assignmentId: number | undefined) {
  return useQuery<Certificate>({
    queryKey: ["managerCertificate", assignmentId],
    queryFn: () =>
      api.get<Certificate>(
        `/api/v1/manager/quiz_assignments/${assignmentId}/certificate`
      ),
    enabled: !!assignmentId,
    retry: false,
  })
}

// Staff: Quiz Assignments queries
// `enabled` lets the layout hold this back until the signed-in user has
// resolved, while the training page — which only renders once they have —
// calls it with no argument.
export function useMyAssignments(options?: { enabled?: boolean }) {
  return useQuery<StaffAssignmentRow[]>({
    queryKey: ["myAssignments"],
    queryFn: () =>
      api.get<StaffAssignmentRow[]>("/api/v1/customer/quiz_assignments"),
    enabled: options?.enabled ?? true,
  })
}

// Staff: Quiz Attempts queries

// Start a new attempt or resume the one already open. The server decides
// which; `outcome` says what happened so the page can explain a restart.
export function useStartAttempt() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (assignmentId: number) =>
      api.post<StartedAttempt>(
        `/api/v1/customer/quiz_assignments/${assignmentId}/attempts`
      ),
    onSuccess: (_data, assignmentId) => {
      queryClient.invalidateQueries({ queryKey: ["myAssignments"] })
      queryClient.invalidateQueries({ queryKey: ["assignmentDetail", assignmentId] })
    },
  })
}

// Fired on every answer selection, so a closed tab loses nothing.
export function useSaveResponse() {
  return useMutation({
    mutationFn: (data: {
      attempt_id: number
      question_id: number
      answer_option_id: number
    }) =>
      api.patch<{ question_id: number; answer_option_id: number }>(
        `/api/v1/customer/quiz_attempts/${data.attempt_id}/responses`,
        { question_id: data.question_id, answer_option_id: data.answer_option_id }
      ),
  })
}

export function useSubmitAttempt() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (attemptId: number) =>
      api.post<SubmitResult>(`/api/v1/customer/quiz_attempts/${attemptId}/submit`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["myAssignments"] })
      queryClient.invalidateQueries({ queryKey: ["assignmentDetail"] })
    },
  })
}

// Attempt history. Overall result per attempt — never per-question detail.
export function useAssignmentDetail(assignmentId: number | undefined) {
  return useQuery<AssignmentDetail>({
    queryKey: ["assignmentDetail", assignmentId],
    queryFn: () =>
      api.get<AssignmentDetail>(
        `/api/v1/customer/quiz_assignments/${assignmentId}`
      ),
    enabled: !!assignmentId,
  })
}

export function useCertificate(attemptId: number | undefined) {
  return useQuery<Certificate>({
    queryKey: ["certificate", attemptId],
    queryFn: () =>
      api.get<Certificate>(
        `/api/v1/customer/quiz_attempts/${attemptId}/certificate`
      ),
    enabled: !!attemptId,
    retry: false,
  })
}

export function useAttemptReview(attemptId: number | undefined) {
  return useQuery<AttemptReview>({
    queryKey: ["attemptReview", attemptId],
    queryFn: () =>
      api.get<AttemptReview>(
        `/api/v1/customer/quiz_attempts/${attemptId}/review`
      ),
    enabled: !!attemptId,
  })
}

export function useSubmitFeedback() {
  return useMutation({
    mutationFn: (data: {
      quiz_assignment_id: number
      // The star rating is retired from the UI. The endpoint and column still
      // accept it, so bringing it back needs no server change.
      rating?: number
      comment: string
    }) => api.post<{ id: number }>("/api/v1/customer/quiz_feedbacks", data),
  })
}
